import { fn } from '@ember/helper';
import { action, get, set } from '@ember/object';
import { trackedSet } from '@ember/reactive/collections';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { CURRENCY_MAPPING } from 'frontend-utils/utils/constants/currency-mapping-constants';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralTooltipsTooltipRatesCalendar from 'lumen/components/general/tooltips/tooltip-rates-calendar';
import { collectMeanData } from 'lumen/utils/charts/chart-utils';
import {
  calculateYaxisMax,
  connectRateDataPoint,
  connectUndefinedRateDataPoint,
  getSeriesConfiguration,
} from 'lumen/utils/charts/rates-chart-utils';
import { SHORT_TERM_RENTAL_RATE_COLOR } from 'lumen/utils/constants/chart-color-constants';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';
import {
  getDefaultChartOptions,
  getDefaultTooltipOptions,
} from 'lumen/utils/rates-chart';

const NUM_Y_TICKS = 11;

// The margin of units (dollars, euros, ...) where we show two competitors as highlighted
const RATES_CHART_TOOLTIP_MARGIN = 4;

const SERIES = {
  DUMMY: 'dummy',
  OTB: 'otb',
  MARKET_DEMAND: 'market-demand',
  MEDIAN_COMP_RATE: 'median-comp-rate',
  RANGE: 'range',
  SHORT_TERM_RENTAL: 'short-term-rental',
};

interface GeneralDayDetailChartsChartRatesEvolutionSignature {
  Args: {
    competitorsForCurrentHotelAndOTA: unknown;
    containerHeight: unknown;
    correctedWidth: unknown;
    currency: unknown;
    dayMoment: unknown;
    hotelObject: unknown;
    isBestFlex: unknown;
    isLoading: unknown;
    otaId: unknown;
    params: unknown;
    periods: unknown;
    rentalData: unknown;
    sortedPeriodsOTB: unknown;
  };
}

export default class GeneralDayDetailChartsChartRatesEvolution extends Component<GeneralDayDetailChartsChartRatesEvolutionSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare rates: Services['rates'];

  hiddenSeries = trackedSet([SERIES.OTB]);
  tooltip = getDefaultTooltipOptions();

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // We added this because the chartData needs to be regenerated,
    // if the day request ends later than the period request (rates route)
    // @ts-expect-error: Incorrect type
    if (this.currentHotel?.loading) {
      return null;
    }

    // Get default rates from own and compset hotels
    const { data, meanData } = this.ratesData;
    const dummySeries = this.getEmptySeries(meanData);

    const chartData = [];

    // Check OTB data feature in evolution chart
    if (this.hasOTB) {
      chartData.push({
        color: 'var(--prism-color-general-neutral-600)',
        data: this.OTBData,
        id: SERIES.OTB,
        marker: {
          enabled: false,
        },
        name: this.intl.t('rates.OTB'),
        type: 'line',
        visible: !this.hiddenSeries.has(SERIES.OTB),
        yAxis: 1,
        zIndex: 20,
        zoneAxis: 'x',
        zones: [],
      });
    }

    if (dummySeries) {
      chartData.push({
        color: 'var(--prism-color-chart-status-figure-warning-normal)',
        data: dummySeries,
        id: SERIES.DUMMY,
        name: 'dummy',
        showInLegend: false,
        type: 'line',
        visible: true,
        zIndex: 10,
      });
    }

    // @ts-expect-error: Incorrect type
    return chartData.concat(data);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    const { containerHeight, correctedWidth } = this.args;

    const defaultOptions = getDefaultChartOptions(
      this.xAxis,
      this.yAxis,
      this.tooltip,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const offsetHeight =
      // @ts-expect-error: Incorrect type
      document.querySelector('#legend-container')?.offsetHeight ?? 0;

    return {
      ...defaultOptions,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      chart: {
        // @ts-expect-error: Incorrect type
        ...defaultOptions.chart,
        // @ts-expect-error: Incorrect type
        height: containerHeight - offsetHeight,
        width: correctedWidth,
      },
      legend: {
        enabled: false,
      },
      title: {
        text: 'rates-evolution-chart',
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentHotel() {
    return this.args.hotelObject;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasOTB() {
    // @ts-expect-error: Incorrect type
    return this.access.hasBookingCurveOnDate(this.args.dayMoment);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEmpty() {
    const { competitorsForCurrentHotelAndOTA, isLoading, periods } = this.args;

    // Don't show NO DATA when still loading
    // @ts-expect-error: Incorrect type
    if (isLoading || this.currentHotel?.loading) {
      return false;
    }

    // Get current hotel and all of its competitors
    // @ts-expect-error: Incorrect type
    let hotels = [this.currentHotel?.hotelinfo];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    hotels = [...hotels, ...competitorsForCurrentHotelAndOTA];

    // For all periods and per all collected hotels, check if all of them are empty, only show NO DATA when all of them are...
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return periods.every((period) => {
      return hotels.every((hotel) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const rate = get(period, `rates.${hotel.id}`);

        if (rate === undefined) {
          return true;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const firstRate = rate[0];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!firstRate || !firstRate.value) {
          return true;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return firstRate.message === 'general.missing';
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get legendData() {
    // ChartData is null when currentHotel.isHotelLoading is true
    return this.chartData?.filter((series) => {
      return !(series.id === SERIES.DUMMY || series.id === SERIES.RANGE);
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get OTBData() {
    // @ts-expect-error: Incorrect type
    const data = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.sortedPeriodsOTB.forEach((period) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const isAfterIntegrationDate = period.extractDate.isAfter(
        this.hotelAndMarketService.hotel?.pmsIntegrationDate,
      );

      // Need to check for null
      // If you visited a day with data on a certain leadtime, and one without, it will be mapped to 0
      if (
        isAfterIntegrationDate &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        period.occupancy?.occupancyPercentage !== null
      ) {
        data.push([
          this.xValue(period),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          period.occupancy?.occupancyPercentage * 100,
        ]);
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return data;
  }

  /**
   * Own + competitors' rates
   * @return {Object} Object containing the data which can be displayed in the chart + the collected mean data
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesData() {
    const { competitorsForCurrentHotelAndOTA } = this.args;
    const { yAxisMax } = this;

    const ownHotelAndCompetitors = [
      // @ts-expect-error: Incorrect type
      this.currentHotel?.hotelinfo,
      // @ts-expect-error: Incorrect type
      ...competitorsForCurrentHotelAndOTA,
    ];

    // Will be used to determine median and range series
    const meanData = new Array(ownHotelAndCompetitors.length);

    const data = ownHotelAndCompetitors.map((hotel) =>
      this.collectRatesForHotel(hotel, yAxisMax, meanData),
    );

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      // @ts-expect-error: Incorrect type
      data.push(this.collectShortTermRentalData());
    }

    return { data, meanData };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rentalRates() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.rentalData?.rates;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get xAxis() {
    return {
      allowDecimals: false,
      labels: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          return `${Math.abs(this.value)}`;
        },
      },
      title: {
        text: this.intl.t('rates.leadtime'),
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxis() {
    const { currency } = this.args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const axis = [
      {
        // Add a margin, if the largest point is equal to yAxisMax the point woint be visible
        ceiling: this.yAxisMax + 5,
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            if (currency) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const currencySymbol = CURRENCY_MAPPING[currency] ?? currency;

              const label = that.rates.formatValue(
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.value,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                { longFormat: false, currencySymbol },
              );

              return `${label}`;
            }

            const label = that.rates.formatRate(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              // @ts-expect-error: Incorrect type
              that.currentHotel,
              { longFormat: false },
            );

            return label === null ? label : `${label}`;
          },
        },
        showFirstLabel: false,
        tickAmount: NUM_Y_TICKS,
        title: {
          text: '',
        },
      },
    ];

    if (this.hasOTB) {
      axis.push({
        ceiling: 100,
        labels: {
          formatter() {
            // @ts-expect-error: Incorrect type
            return `${this.value} %`;
          },
        },
        // @ts-expect-error: Incorrect type
        min: 0,
        minRange: 100,
        opposite: true,
        showEmpty: false,
        showFirstLabel: false,
        tickAmount: NUM_Y_TICKS,
        title: {
          text: '',
        },
      });
    }

    return axis;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  @cached get yAxisMax() {
    const { competitorsForCurrentHotelAndOTA, periods } = this.args;

    const yAxisMax = calculateYaxisMax(
      this.currentHotel,
      competitorsForCurrentHotelAndOTA,
      periods,
    );

    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.rentalRates.length &&
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const rentalRateValues = this.rentalRates.map((rate) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return rate.value ?? Number.MIN_VALUE;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return Math.max(yAxisMax, ...rentalRateValues);
    }

    return yAxisMax;
  }

  constructor(
    // @ts-expect-error: Incorrect type
    owner,
    args: GeneralDayDetailChartsChartRatesEvolutionSignature['Args'],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    super(owner, args);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    set(this, 'tooltip.formatter', function () {
      /*
      The formatter will set the necessary value needed for the tooltip component.
      The highcharts formatter requires html to be returned (in this case)
      so we need to get the rendered html from the template
      The tooltip component is placed in a dummy div where this html can be obtained.
      Caution: the data for the component needs to be set in a different run loop,
      otherwise stale data will be returned
      */
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const { chart } = this.series;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { series } = chart;

      let OTBSeriesIndex = 0;
      let rateSeriesIndex = 0;

      let periodIndex = 0;
      let sortedPeriodIndex = 0;

      if (that.hasOTB) {
        // Search and stop when you find the OTB series index
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        OTBSeriesIndex = series.findIndex(({ name }) => {
          return name === that.intl.t('rates.OTB');
        });

        while (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          series[OTBSeriesIndex]?.data[sortedPeriodIndex] &&
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.x !== series[OTBSeriesIndex].data[sortedPeriodIndex].x
        ) {
          sortedPeriodIndex += 1;
        }

        // The while loop above assumes a match is always found
        // However: if we are looking at a date before the integration date, there is no match
        // We set the sortedPeriodIndex to -1 in that case to make sure the MY OTB section is not shown in the tooltip header
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!series[OTBSeriesIndex].data[sortedPeriodIndex]) {
          sortedPeriodIndex = -1;
        }
      }

      // Search and stop when you find any Rate series index
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      rateSeriesIndex = series.findIndex(({ data, name }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return name !== that.intl.t('rates.OTB') && data.length > 0;
      });

      // Compute the index of corr y value in each data arrays
      while (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        series[rateSeriesIndex]?.data[periodIndex] &&
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.x !== series[rateSeriesIndex].data[periodIndex].x
      ) {
        periodIndex += 1;
      }

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const yValue = this.y;
      // @ts-expect-error: Incorrect type
      const hoveredHotels = [];

      // eslint-disable-next-line ember/no-runloop
      begin();
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      set(that, 'hoveredRateValue', this.point.y);

      if (that.hasOTB) {
        set(
          that,
          'periodForEvolutionTooltipHeader',
          // @ts-expect-error: Incorrect type
          that.args.sortedPeriodsOTB[sortedPeriodIndex],
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const rentalRate = that.rentalRates[periodIndex];
      set(that, 'rentalRate', rentalRate);
      // @ts-expect-error: Incorrect type
      set(that, 'period', that.args.periods[periodIndex]);
      set(that, 'series', series);

      // Find the median
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      series.forEach(({ data, name }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (data.length === 0) {
          return;
        }

        // Give some margin for rates that are practically the same
        // Some Y values may be null (sold out,...); check is needed because of that
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          data[periodIndex]?.y &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          Math.abs(data[periodIndex].y - yValue) < RATES_CHART_TOOLTIP_MARGIN
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          hoveredHotels.push(data[periodIndex].hotelId);
        }

        if (name === that.intl.t('rates.median_comp_rate')) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          set(that, 'median', data[periodIndex].tooltipValue);
        }
      });
      // @ts-expect-error: Incorrect type
      set(that, 'hoveredHotels', hoveredHotels);
      // eslint-disable-next-line ember/no-runloop
      end();

      // @ts-expect-error: Incorrect type
      return document.getElementById('dummy-div-for-tooltip-rates-evolution')
        .innerHTML;
    });
  }

  /**
   * Gets all periods for a given hotel and maps them to an array of points
   * These points can be used directly in a rates-chart
   * @param  {Object}  hotel    The hotel object for which to collect points
   * @param  {Number}  yAxisMax The current max value on the y-axis
   * @param  {Object}  meanData A collection used to keep track of mean data
   * @return {Array}            Returns an array containing the datapoints for the chart
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  collectRatesForHotel(hotel, yAxisMax, meanData) {
    const { periods } = this.args;

    // Determine points if previous is Sold out, LOS2,...
    let previousValueX = 0;
    // @ts-expect-error: Incorrect type
    let previousValueY = null;
    // @ts-expect-error: Incorrect type
    const zones = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isCurrentHotel = hotel.id === this.currentHotel?.hotelinfo?.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const hotelId = hotel.id;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const current = periods
      // @ts-expect-error: Incorrect type
      .map((period, index) => {
        // Rates for current hotel
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const compRates = get(period, `rates.${hotel.id}`);

        if (compRates !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const selectedRate = compRates[0];
          const isSoldOut =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !selectedRate || !selectedRate.value || selectedRate.value === 0;

          const xValue = this.xValue(period);
          const xSoldOut = this.xSoldOut(period);

          let dataPoint;

          if (!isSoldOut) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            [dataPoint, previousValueX, previousValueY] = connectRateDataPoint(
              this.hotelAndMarketService,
              xValue,
              xSoldOut,
              // @ts-expect-error: Incorrect type
              zones,
              selectedRate,
              yAxisMax,
              hotelId,
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            dataPoint = {
              ...dataPoint,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              membershiptype: selectedRate.membershiptype,
            };

            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
            collectMeanData(meanData, dataPoint, isCurrentHotel, index);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return dataPoint;
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [dataPoint, previousValueX] = connectUndefinedRateDataPoint(
            previousValueX,
            // @ts-expect-error: Incorrect type
            previousValueY,
            // @ts-expect-error: Incorrect type
            zones,
            xValue,
            xSoldOut,
            index,
            selectedRate,
            hotelId,
          );

          // Collect the mean data for later median calculus
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
          collectMeanData(meanData, dataPoint, isCurrentHotel, index);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return dataPoint;
        }
        return undefined;
      })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter(Boolean);

    const hotelSeriesConfig = getSeriesConfiguration(
      hotel,
      current,
      isCurrentHotel,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.activeCompset?.competitors,
      // @ts-expect-error: Incorrect type
      zones,
    );

    const series = {
      ...hotelSeriesConfig,
      // make sure the hotel is only visible if it is the currenthotel,
      // or if this is the rate evolution chart, in which case the hotels are always visible
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      visible: !that.hiddenSeries.has(hotel.id),
    };

    // Honestly don't know why the other zIndex does not work in the rates evolution chart,
    // seems to be a problem with the arearange
    series.zIndex = isCurrentHotel ? 2 : 1;

    return series;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  collectShortTermRentalData() {
    const { periods, rentalData } = this.args;

    const series = {
      color: SHORT_TERM_RENTAL_RATE_COLOR,
      data: [],
      id: SERIES.SHORT_TERM_RENTAL,
      name: this.intl.t('short_term_rental.rate'),
      type: 'line',
      visible: !this.hiddenSeries.has(SERIES.SHORT_TERM_RENTAL),
      zIndex: 20,
    };

    const rentalRatesMap = new Map();

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    rentalData.rates.forEach((rate) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      rentalRatesMap.set(rate.formattedExtractDate, rate);
    });

    // @ts-expect-error: Incorrect type
    let previousValueY = null;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    periods.forEach((period) => {
      let rentalRateForExtractDate;

      // In the day detail view we don't have a reference to the individual Periods
      // Therefore we need to manually pass rentalData and find it by matching the extractDate (using Map to avoid nested loop)
      // Ref https://otainsight.slack.com/archives/C0461CHQ6QL/p1669993593666339?thread_ts=1669105583.830299&cid=C0461CHQ6QL
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, prefer-const
      rentalRateForExtractDate = rentalRatesMap.get(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        period.extractDate.format('YYYY-MM-DD'),
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { isMissing } = rentalRateForExtractDate;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const y = isMissing ? previousValueY : rentalRateForExtractDate.value;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousValueY = isMissing
        ? // @ts-expect-error: Incorrect type
          previousValueY
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rentalRateForExtractDate.value;

      const seriesData = {
        hotelId: SERIES.SHORT_TERM_RENTAL,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        tooltipValue: Math.round(rentalRateForExtractDate.value),
        x: this.xValue(period),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y,
      };

      if (isMissing) {
        // @ts-expect-error: Incorrect type
        seriesData.marker = HIGHCHARTS_MISSING_MARKER;
      }

      // @ts-expect-error: Incorrect type
      series.data.push(seriesData);
    });

    return series;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getEmptySeries(data) {
    // @ts-expect-error: Incorrect type
    const dummySeries = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    data.forEach((datum) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const ownHotelPeriods = datum.filter(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        ({ isCurrentHotel }) => isCurrentHotel,
      );

      // Get all hotels, on the condition that they are not sold out, LOS2,...
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const filteredPeriods = datum.filter(({ tooltipValue, y }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return y && tooltipValue && tooltipValue !== 0;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (filteredPeriods.length === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const { x, y } = ownHotelPeriods[0];

        dummySeries.push({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: y ?? 0,
          marker: HIGHCHARTS_MISSING_MARKER,
        });
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (dummySeries.length === data.length) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return dummySeries;
    }

    return null;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xSoldOut(period) {
    return this.xValue(period);
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xValue(period) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return -1 * period.leadtime;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  @action isSeriesHidden(series) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    return this.hiddenSeries.has(series.id ?? series.hotelId);
  }

  // @ts-expect-error: Incorrect type
  @action toggleEntireCompset(isToggledOn): void {
    const { competitorsForCurrentHotelAndOTA } = this.args;

    if (isToggledOn) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      competitorsForCurrentHotelAndOTA.forEach((competitor) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.hiddenSeries.delete(competitor.id);
      });

      return;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    competitorsForCurrentHotelAndOTA.forEach((competitor) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.hiddenSeries.add(competitor.id);
    });
  }

  // @ts-expect-error: Incorrect type
  @action toggleSeries(legendItem): void {
    // id is undefined for own hotel, competitors and short-term rental rate (see rates-chart-utils.js)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const legendItemId = legendItem.id ?? legendItem.hotelId;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const wasHidden = this.hiddenSeries.has(legendItemId);

    if (wasHidden) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.hiddenSeries.delete(legendItemId);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.hiddenSeries.add(legendItemId);
    }

    if (legendItemId === SERIES.MEDIAN_COMP_RATE) {
      // Show all competitors if the median is toggled off (and vice versa)
      this.toggleEntireCompset(this.hiddenSeries.has(SERIES.MEDIAN_COMP_RATE));
    }

    if (legendItemId === SERIES.OTB) {
      // Show all competitors if OTB is toggled off (and vice versa)
      this.toggleEntireCompset(this.hiddenSeries.has(SERIES.OTB));
    }
  }

  <template>
    <div class="position-relative">
      <GeneralChartsOtaChart
        @chartOptions={{this.chartOptions}}
        {{! @glint-expect-error: Incorrect type }}
        @model={{this.chartData}}
      />

      <div
        class="chart-legend flex-wrap gap-200 pt-400 pb-400"
        id="legend-container"
      >
        {{#each this.legendData as |legendItem|}}
          {{! @glint-expect-error: Incorrect type }}
          <GeneralChartsLegendsLegendItem
            @color={{legendItem.color}}
            @isHidden={{this.isSeriesHidden legendItem}}
            @legendItemClicked={{fn this.toggleSeries legendItem}}
            @name={{legendItem.name}}
            @type={{legendItem.type}}
          >
            {{legendItem.name}}
          </GeneralChartsLegendsLegendItem>
        {{/each}}
      </div>

      {{#if this.isEmpty}}
        <div class="message-overlay">
          <div class="message-wrapper">
            {{t "general.no_data"}}
          </div>
        </div>
      {{/if}}

      <div class="hidden" id="dummy-div-for-tooltip-rates-evolution">
        <div class="rates-calendar-tooltip">
          <GeneralTooltipsTooltipRatesCalendar
            {{! @glint-expect-error: Incorrect type }}
            @competitorsForCurrentHotelAndOTA={{@competitorsForCurrentHotelAndOTA}}
            {{! @glint-expect-error: Incorrect type }}
            @currency={{@currency}}
            @hideUpdateTime={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @hoveredHotels={{this.hoveredHotels}}
            @isBestFlex={{@isBestFlex}}
            @isRatesEvolution={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @otaId={{@otaId}}
            {{! @glint-expect-error: Incorrect type }}
            @params={{@params.ratesParams}}
            {{! @glint-expect-error: Incorrect type }}
            @passedHotel={{@hotelObject}}
            {{! @glint-expect-error: Incorrect type }}
            @period={{this.period}}
            {{! @glint-expect-error: Incorrect type }}
            @periodForEvolutionTooltipHeader={{this.periodForEvolutionTooltipHeader}}
            {{! @glint-expect-error: Incorrect type }}
            @rentalRate={{this.rentalRate}}
            {{! @glint-expect-error: Incorrect type }}
            @series={{this.series}}
          />
        </div>
      </div>
    </div>
  </template>
}
